import { Button, Card, CardContent, CardHeader, CardTitle, StatusBadge } from '@app/ui';
import { PageTransition, StaggerContainer, StaggerItem } from '@app/ui/motion';
import { formatCurrency, formatDateShort } from '@app/validators';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { MissingNcaaIdBanner } from '../../components/missing-ncaa-id-banner';
import { api } from '../../lib/api';
import { getDashboardData } from '../../lib/dashboard-server';

export const Route = createFileRoute('/_authenticated/dashboard')({
  loader: async ({ context }) => {
    // US-031: user.me feeds the missing-NCAA-ID banner; fetched FIRST
    // because it also decides the persona routing below.
    const { getServerApi } = await import('../../lib/api-server');
    const serverApi = await getServerApi();
    const user = await context.queryClient.ensureQueryData(serverApi.user.me.queryOptions());

    // This dashboard rolls up deals the signed-in user OWNS — an agent
    // owns none, so for the agent persona it reads zero forever and looks
    // broken (founder report 2026-08-15). Their real numbers (athletes,
    // tracked value, est. commission) live on the agency roster — send
    // them there. Keyed on the EXPLICIT persona choice, not agency
    // membership, so a dual-role user who onboarded as an athlete keeps
    // their own dashboard.
    if (user?.userType === 'agent') {
      throw redirect({ to: '/agency/roster' });
    }

    // Dashboard rollup via the server-fn; eager-set so useSuspenseQuery
    // renders without an extra client roundtrip.
    const data = await getDashboardData();
    context.queryClient.setQueryData(api.deal.dashboard.queryOptions().queryKey, data);
  },
  pendingComponent: DashboardSkeleton,
  errorComponent: ({ error }) => (
    <div className="text-center py-12">
      <p className="text-red-600">
        Failed to load dashboard: {error instanceof Error ? error.message : 'Something went wrong'}
      </p>
    </div>
  ),
  component: Dashboard,
});

function Dashboard() {
  const { data } = useSuspenseQuery(api.deal.dashboard.queryOptions());
  // US-031: small extra query so the banner can read ncaaId without
  // bloating the dashboard rollup. user.me is cached and fast.
  const { data: user } = useSuspenseQuery(api.user.me.queryOptions());

  return (
    <PageTransition>
      <MissingNcaaIdBanner user={user} />

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold">Dashboard</h1>
        <Link to="/deals/new">
          <Button className="min-h-[44px]">New Deal</Button>
        </Link>
      </div>

      <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StaggerItem>
          <MetricCard title="Total Deals" value={String(data.totalDeals)} />
        </StaggerItem>
        <StaggerItem>
          <MetricCard title="Active Deals" value={String(data.activeDeals)} />
        </StaggerItem>
        <StaggerItem>
          <MetricCard title="Total Earned" value={formatCurrency(data.totalEarnings)} />
        </StaggerItem>
        <StaggerItem>
          <MetricCard title="Pending Payments" value={formatCurrency(data.pendingPayments)} />
        </StaggerItem>
      </StaggerContainer>

      <StaggerContainer delay={0.2} className="grid lg:grid-cols-2 gap-6">
        <StaggerItem>
          <Card>
            <CardHeader>
              <CardTitle>Recent Deals</CardTitle>
            </CardHeader>
            <CardContent>
              {data.recentDeals.length === 0 ? (
                <p className="text-muted-foreground text-sm">
                  No deals yet.{' '}
                  <Link to="/deals/new" className="underline">
                    Create your first deal
                  </Link>
                </p>
              ) : (
                <div className="space-y-3">
                  {data.recentDeals.map((deal) => (
                    <Link
                      key={deal.id}
                      to="/deals/$dealId"
                      params={{ dealId: deal.id }}
                      className="flex items-center justify-between py-2 px-3 rounded-md hover:bg-secondary/50 transition-colors"
                    >
                      <div>
                        <p className="text-sm font-medium">{deal.title}</p>
                        <p className="text-xs text-muted-foreground">{deal.brandName}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium">{formatCurrency(deal.amount)}</p>
                        <StatusBadge status={deal.status} />
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </StaggerItem>

        <StaggerItem>
          <Card>
            <CardHeader>
              <CardTitle>Upcoming Deadlines</CardTitle>
            </CardHeader>
            <CardContent>
              {data.upcomingDeadlines.length === 0 ? (
                <p className="text-muted-foreground text-sm">No upcoming deadlines.</p>
              ) : (
                <div className="space-y-3">
                  {data.upcomingDeadlines.map((d) => (
                    <div key={d.id} className="flex items-center justify-between py-2">
                      <div>
                        <p className="text-sm font-medium">{d.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {d.brandName} &middot; {d.dealTitle}
                        </p>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {d.dueDate ? formatDateShort(d.dueDate) : '--'}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </StaggerItem>
      </StaggerContainer>
    </PageTransition>
  );
}

function MetricCard({ title, value }: { title: string; value: string }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <p className="text-sm text-muted-foreground">{title}</p>
        <p className="text-2xl font-semibold mt-1">{value}</p>
      </CardContent>
    </Card>
  );
}

function DashboardSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="h-8 bg-muted rounded w-48 mb-8" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i}>
            <CardContent className="pt-6">
              <div className="h-4 bg-muted rounded w-20 mb-2" />
              <div className="h-8 bg-muted rounded w-16" />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
